/**
 * 템플릿 갤러리용 IndexedDB 유틸리티
 */

export interface TemplateInfo {
  id: string;
  name: string;
  thumbnailPaths: string[]; // 썸네일 이미지 파일 경로 배열
  thumbnails?: string[]; // 썸네일 base64 이미지 배열 (IndexedDB에 저장)
  folderPath?: string; // 폴더 경로 (선택적)
  lastModified?: number; // 마지막 수정 시간
}

interface TemplateGalleryDB {
  templates: TemplateInfo[];
  folderHandle?: FileSystemDirectoryHandle; // File System Access API 핸들 (직렬화 불가, 별도 저장 필요)
}

const DB_NAME = 'TemplateGalleryDB';
const DB_VERSION = 1;
const STORE_NAME = 'templates';

/**
 * IndexedDB 열기
 */
async function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

/**
 * 템플릿 목록 저장
 */
export async function saveTemplates(templates: TemplateInfo[]): Promise<void> {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    // 기존 데이터 모두 삭제
    await new Promise<void>((resolve, reject) => {
      const clearRequest = store.clear();
      clearRequest.onsuccess = () => resolve();
      clearRequest.onerror = () => reject(clearRequest.error);
    });

    // 새 데이터 추가
    for (const template of templates) {
      await new Promise<void>((resolve, reject) => {
        const addRequest = store.add(template);
        addRequest.onsuccess = () => resolve();
        addRequest.onerror = () => reject(addRequest.error);
      });
    }
  } catch (error) {
    console.error('템플릿 목록 저장 실패:', error);
    throw error;
  }
}

/**
 * 템플릿 목록 불러오기
 */
export async function loadTemplates(): Promise<TemplateInfo[]> {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('템플릿 목록 불러오기 실패:', error);
    return [];
  }
}

/**
 * 템플릿 목록 삭제
 */
export async function clearTemplates(): Promise<void> {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const clearRequest = store.clear();
      clearRequest.onsuccess = () => resolve();
      clearRequest.onerror = () => reject(clearRequest.error);
    });
  } catch (error) {
    console.error('템플릿 목록 삭제 실패:', error);
    throw error;
  }
}

